/**
 * ApplicantTypeSelector Component
 * 
 * Displays MRS and MISS options for applicant type selection.
 * When Miss is selected, shows a secondary marital status sub-selection
 * with options: Single, Divorced, Widowed.
 * Shows validation error if user tries to proceed without selection.
 * Calls onSelect callback with the selected type.
 * 
 * Requirements: 1.1, 1.2, 1.5, 3.1, 3.2, 3.4, 3.5, 12.1 - Applicant type selection and marital status
 */

import React, { useState, useCallback } from 'react';
import type { ApplicantType, MaritalStatus } from '@/types';

export interface ApplicantTypeSelectorProps {
  /** Currently selected applicant type */
  value?: ApplicantType | null;
  /** Callback when an applicant type is selected */
  onSelect: (type: ApplicantType) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether to show validation error (e.g., when user tries to proceed without selection) */
  showError?: boolean;
  /** Custom error message */
  errorMessage?: string;
  /** Currently selected marital status (Miss contestants only) */
  maritalStatus?: MaritalStatus | null;
  /** Callback when a marital status is selected */
  onMaritalStatusSelect?: (status: MaritalStatus) => void;
  /** Whether to show marital status validation error */
  showMaritalStatusError?: boolean;
  /** Custom marital status error message */
  maritalStatusErrorMessage?: string;
}

const APPLICANT_TYPE_OPTIONS: { value: ApplicantType; label: string; description: string; subtext?: string }[] = [
  {
    value: 'MRS',
    label: 'Mrs. Idaho',
    description: 'For married women competing in the Mrs. Idaho pageant',
  },
  {
    value: 'MISS',
    label: 'Miss Idaho',
    description: 'For single women competing in the Miss Idaho pageant',
    subtext: 'If there are less than 5 Miss applicants, the Miss Idaho for America Strong will revert to an "at-large" title.',
  },
];

const MARITAL_STATUS_OPTIONS: { value: MaritalStatus; label: string }[] = [
  { value: 'SINGLE', label: 'Single' },
  { value: 'DIVORCED', label: 'Divorced' },
  { value: 'WIDOWED', label: 'Widowed' },
];

/**
 * ApplicantTypeSelector allows users to select their competition category.
 * Displays MRS and MISS options with descriptions.
 * When Miss is selected, shows a marital status sub-selection.
 * Shows validation error if no selection is made when required.
 */
export function ApplicantTypeSelector({
  value,
  onSelect,
  disabled = false,
  showError = false,
  errorMessage = 'Please select an applicant type to continue',
  maritalStatus,
  onMaritalStatusSelect,
  showMaritalStatusError = false,
  maritalStatusErrorMessage = 'Please select a marital status to continue',
}: ApplicantTypeSelectorProps): React.ReactElement {
  const [internalError, setInternalError] = useState(false);

  const handleSelect = useCallback(
    (type: ApplicantType) => {
      if (disabled) return;
      setInternalError(false);
      onSelect(type);
    },
    [disabled, onSelect]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, type: ApplicantType) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect(type);
      }
    },
    [disabled, handleSelect]
  );

  const handleMaritalStatusSelect = useCallback(
    (status: MaritalStatus) => {
      if (disabled) return;
      onMaritalStatusSelect?.(status);
    },
    [disabled, onMaritalStatusSelect]
  );

  const handleMaritalStatusKeyDown = useCallback(
    (event: React.KeyboardEvent, status: MaritalStatus) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleMaritalStatusSelect(status);
      }
    },
    [disabled, handleMaritalStatusSelect]
  );

  const displayError = showError || internalError;

  return (
    <div role="group" aria-labelledby="applicant-type-label">
      <h2
        id="applicant-type-label"
        className="font-headline text-2xl font-bold text-foreground mb-2"
      >
        Select Your Competition Category
      </h2>
      
      <p className="font-body text-muted-foreground mb-6">
        Please select the category you wish to compete in. This will determine the form fields and document requirements for your application.
      </p>

      {displayError && (
        <div 
          className="bg-destructive-muted text-destructive-text rounded-lg p-3 mb-4 font-body text-sm" 
          role="alert" 
          aria-live="assertive"
          id="applicant-type-error"
        >
          {errorMessage}
        </div>
      )}

      <div 
        className="flex flex-col gap-4" 
        role="radiogroup" 
        aria-required="true"
        aria-describedby={displayError ? 'applicant-type-error' : undefined}
      >
        {APPLICANT_TYPE_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          const showMaritalStatus = option.value === 'MISS' && value === 'MISS';
          
          return (
            <React.Fragment key={option.value}>
              <div
                className={[
                  'flex items-center gap-4 p-5 rounded-card cursor-pointer transition-all duration-300 ease-out',
                  isSelected
                    ? 'selected bg-card shadow-subtle border border-primary/20'
                    : 'bg-card hover:bg-muted border border-border/15',
                  disabled ? 'disabled opacity-50 cursor-not-allowed' : '',
                ].join(' ')}
                role="radio"
                aria-checked={isSelected}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : 0}
                onClick={() => handleSelect(option.value)}
                onKeyDown={(e) => handleKeyDown(e, option.value)}
                data-testid={`applicant-type-${option.value.toLowerCase()}`}
              >
                <div className="shrink-0" aria-hidden="true">
                  <span
                    className={[
                      'block w-5 h-5 rounded-full border-2 transition-all duration-300 ease-out',
                      isSelected
                        ? 'checked bg-gradient-to-br from-primary to-primary-container border-primary shadow-raised'
                        : 'bg-muted border-border/50',
                    ].join(' ')}
                  />
                </div>
                
                <div className="flex flex-col gap-1">
                  <span className="font-headline text-sm font-bold text-foreground">
                    {option.label}
                  </span>
                  <span className="font-body text-xs text-muted-foreground">
                    {option.description}
                  </span>
                  {option.subtext && (
                    <span className="font-body text-[10px] text-gold-500 italic mt-1">
                      {option.subtext}
                    </span>
                  )}
                </div>
              </div>

              {showMaritalStatus && (
                <div
                  className="ml-9 mt--2"
                  data-testid="marital-status-section"
                >
                  <div
                    role="group"
                    aria-labelledby="marital-status-label"
                  >
                    <h3
                      id="marital-status-label"
                      className="font-headline text-xs font-bold text-foreground mb-2"
                    >
                      Marital Status
                    </h3>

                    {showMaritalStatusError && (
                      <div
                        className="bg-destructive-muted text-destructive-text rounded-lg p-2 mb-3 font-body text-xs"
                        role="alert"
                        aria-live="assertive"
                        id="marital-status-error"
                      >
                        {maritalStatusErrorMessage}
                      </div>
                    )}

                    <div
                      className="flex flex-col gap-2"
                      role="radiogroup"
                      aria-required="true"
                      aria-describedby={showMaritalStatusError ? 'marital-status-error' : undefined}
                    >
                      {MARITAL_STATUS_OPTIONS.map((statusOption) => {
                        const isStatusSelected = maritalStatus === statusOption.value;

                        return (
                          <div
                            key={statusOption.value}
                            className={[
                              'flex items-center gap-3 px-4 py-3 rounded-lg cursor-pointer transition-all duration-300 ease-out',
                              isStatusSelected
                                ? 'selected bg-card shadow-subtle border border-primary/20'
                                : 'bg-card hover:bg-muted border border-border/15',
                              disabled ? 'disabled opacity-50 cursor-not-allowed' : '',
                            ].join(' ')}
                            role="radio"
                            aria-checked={isStatusSelected}
                            aria-disabled={disabled}
                            tabIndex={disabled ? -1 : 0}
                            onClick={() => handleMaritalStatusSelect(statusOption.value)}
                            onKeyDown={(e) => handleMaritalStatusKeyDown(e, statusOption.value)}
                            data-testid={`marital-status-${statusOption.value.toLowerCase()}`}
                          >
                            <div className="shrink-0" aria-hidden="true">
                              <span
                                className={[
                                  'block w-3.5 h-3.5 rounded-full border-2 transition-all duration-300 ease-out',
                                  isStatusSelected
                                    ? 'checked bg-gradient-to-br from-primary to-primary-container border-primary shadow-raised'
                                    : 'bg-muted border-border/50',
                                ].join(' ')}
                              />
                            </div>

                            <span className="font-headline text-xs font-bold text-foreground">
                              {statusOption.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default ApplicantTypeSelector;
